import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Snackbar,
  Stack,
  Collapse,
  Typography,
  LinearProgress,
} from '@mui/material';
import { 
  Add, 
  Remove, 
  Fullscreen, 
  FullscreenExit, 
  Edit, 
  Delete, 
  Save, 
  Cancel, 
  AddCircle, 
  LinkOff,
  ExpandMore,
  ExpandLess,
  OpenInNew,
  Image,
  VideoLibrary,
  Description,
  Audiotrack,
  TextFields,
  Link,
  CloudUpload,
  Close,
} from '@mui/icons-material';
import { Node, Edge } from 'reactflow';
import { FlowBuilderActions } from '../../../Services/_actions/index.js';
import { useSnackBar } from '../Common/SnackBar/index.tsx';

interface Props {
  open: boolean;
  onClose: () => void;
  nodes: Node[];
  edges: Edge[];
  onUpdateNodes: (nodes: Node[]) => void;
  onUpdateEdges?: (edges: Edge[]) => void;
  onEditNode?: (nodeId: string) => void;
  botFlowData?: any; // Add botFlowData for upload
}

// Node type options for dropdown
const NODE_TYPES = [
  'text',
  'image',
  'video',
  'audio',
  'document',
  'button',
  'list',
  'intervention',
  'catalog',
  'product',
  'whatsAppFlow',
  'existingFlow',
  'address',
  'openAi',
  'ctaUrl',
  'conditional'
];

// Node types that have header
const NODE_TYPES_WITH_HEADER = ['image', 'video', 'audio', 'document', 'button', 'list', 'intervention', 'catalog', 'product', 'whatsAppFlow', 'address', 'ctaUrl'];

// Node types that have footer
const NODE_TYPES_WITH_FOOTER = ['image', 'video', 'audio', 'document', 'button', 'list', 'intervention', 'catalog', 'product', 'whatsAppFlow', 'address', 'ctaUrl'];

// Media node types
const MEDIA_NODE_TYPES = ['image', 'video', 'audio', 'document'];

// Header type options
const HEADER_TYPES = [
  { value: 'text', label: 'Text', icon: <TextFields fontSize="small" /> },
  { value: 'image', label: 'Image', icon: <Image fontSize="small" /> },
  { value: 'video', label: 'Video', icon: <VideoLibrary fontSize="small" /> },
  { value: 'document', label: 'Document', icon: <Description fontSize="small" /> },
  { value: 'audio', label: 'Audio', icon: <Audiotrack fontSize="small" /> },
  { value: 'url', label: 'URL', icon: <Link fontSize="small" /> },
];

// File type configurations
const FILE_CONFIG = {
  image: {
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    maxSize: 10 * 1024 * 1024, // 10MB
    label: 'Image',
    icon: <Image />,
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  },
  video: {
    accept: 'video/mp4,video/webm',
    maxSize: 16 * 1024 * 1024, // 16MB
    label: 'Video',
    icon: <VideoLibrary />,
    extensions: ['.mp4', '.webm']
  },
  audio: {
    accept: 'audio/mpeg,audio/wav,audio/mp3',
    maxSize: 16 * 1024 * 1024, // 16MB
    label: 'Audio',
    icon: <Audiotrack />,
    extensions: ['.mp3', '.wav', '.mpeg']
  },
  document: {
    accept: '.pdf,.doc,.docx,.txt,text/plain',
    maxSize: 100 * 1024 * 1024, // 100MB
    label: 'Document',
    icon: <Description />,
    extensions: ['.pdf', '.doc', '.docx', '.txt']
  }
};

const ShowTableModal: React.FC<Props> = ({ 
  open, 
  onClose, 
  nodes, 
  edges, 
  onUpdateNodes, 
  onUpdateEdges,
  onEditNode,
  botFlowData 
}) => {
  const { showSnackBar } = useSnackBar();
  const actions = FlowBuilderActions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [editableNodes, setEditableNodes] = useState<Node[]>([]);
  const [editableEdges, setEditableEdges] = useState<Edge[]>([]);
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [newNodeType, setNewNodeType] = useState<string>('text');
  const [showNewNodeRow, setShowNewNodeRow] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [uploadingNode, setUploadingNode] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Sync with parent nodes when modal opens or nodes change
  useEffect(() => {
    if (open) {
      setEditableNodes(nodes.map((n) => ({ ...n })));
      setEditableEdges(edges.map((e) => ({ ...e })));
    }
  }, [nodes, edges, open]);

  const handleFieldChange = (id: string, field: string, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, [field]: value } } : node
      )
    );
  };

  // Handle media URL change for media nodes
  const handleMediaUrlChange = (id: string, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== id) return node;
        const fileName = value?.split('/')?.pop() || '';
        return {
          ...node,
          data: {
            ...node.data,
            media: {
              ...node.data.media,
              url: value,
              filename: fileName,
              s3_object: {
                ...node.data.media?.s3_object,
                url: value,
                filename: fileName,
              }
            }
          }
        };
      })
    );
  };

  // Handle file upload for media nodes
  const handleFileUpload = async (nodeId: string, file: File, nodeType: string) => {
    if (!file) return;
    if (!botFlowData?.phone_number_id) {
      showSnackBar('Bot number not found. Please try again.', 'error');
      return;
    }

    const config = FILE_CONFIG[nodeType as keyof typeof FILE_CONFIG];
    if (!config) {
      showSnackBar('Unsupported file type for this node.', 'error');
      return;
    }

    // Validate file size
    if (file.size > config.maxSize) {
      const maxMB = (config.maxSize / (1024 * 1024)).toFixed(0);
      showSnackBar(`File size exceeds ${maxMB}MB limit. Please upload a smaller file.`, 'error');
      return;
    }

    // Validate file type
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!config.extensions.includes(fileExt)) {
      showSnackBar(`Invalid file type. Allowed: ${config.extensions.join(', ')}`, 'error');
      return;
    }

    setUploadingNode(nodeId);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('bot_number', botFlowData?.phone_number_id || '');
      formData.append('uploaded_file', file);
      formData.append('filename', file.name);

      // Simulate progress (since actual upload doesn't have progress)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 10;
          if (newProgress >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 300);

      const resp = await actions.uploadMediaToWhatsapp(formData);
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (resp?.status === 200) {
        const { messaging_product, ...updatedData } = resp?.data;
        
        // Update node with uploaded media
        setEditableNodes((prev) =>
          prev.map((node) => {
            if (node.id !== nodeId) return node;
            return {
              ...node,
              data: {
                ...node.data,
                media: {
                  ...updatedData,
                  filename: file.name,
                }
              }
            };
          })
        );

        showSnackBar(`${config.label} uploaded successfully!`, 'success');
        setUploadProgress(0);
      } else {
        showSnackBar(resp?.response?.data?.detail || 'Error uploading file', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showSnackBar('Error uploading file. Please try again.', 'error');
    } finally {
      setUploadingNode(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleNodeTypeChange = (id: string, newType: string) => {
    setEditableNodes((prev) =>
      prev.map((node) =>
        node.id === id ? { ...node, type: newType } : node
      )
    );
  };

  const handleHeaderTypeChange = (id: string, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) =>
        node.id === id ? { 
          ...node, 
          data: { 
            ...node.data, 
            headerType: { value: value, label: HEADER_TYPES.find(h => h.value === value)?.label || value },
            media: {}
          } 
        } : node
      )
    );
  };

  const handlePositionChange = (id: string, axis: 'x' | 'y', value: number) => {
    setEditableNodes((prev) =>
      prev.map((node) =>
        node.id === id ? { ...node, position: { ...node.position, [axis]: value } } : node
      )
    );
  };

  // Button management
  const handleButtonChange = (nodeId: string, index: number, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const buttons = Array.isArray(node.data?.buttons) ? [...node.data.buttons] : [];
        buttons[index] = value;
        return {
          ...node,
          data: {
            ...node.data,
            buttons: buttons
          }
        };
      })
    );
  };

  const handleAddButton = (nodeId: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const buttons = Array.isArray(node.data?.buttons) ? [...node.data.buttons] : [];
        if (buttons.length < 3) {
          buttons.push(`Button ${buttons.length + 1}`);
        }
        return {
          ...node,
          data: {
            ...node.data,
            buttons: buttons
          }
        };
      })
    );
  };

  const handleRemoveButton = (nodeId: string, index: number) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const buttons = Array.isArray(node.data?.buttons) ? [...node.data.buttons] : [];
        buttons.splice(index, 1);
        const buttonHandle = `${nodeId}-button-${index}`;
        setEditableEdges((prevEdges) => 
          prevEdges.filter((edge) => edge.sourceHandle !== buttonHandle)
        );
        return {
          ...node,
          data: {
            ...node.data,
            buttons: buttons
          }
        };
      })
    );
  };

  // List management
  const handleListOptionChange = (nodeId: string, sectionIndex: number, optionIndex: number, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const options = Array.isArray(node.data?.options) ? [...node.data.options] : [];
        if (options[sectionIndex] && options[sectionIndex].options) {
          options[sectionIndex].options[optionIndex] = value;
        }
        return {
          ...node,
          data: {
            ...node.data,
            options: options
          }
        };
      })
    );
  };

  const handleAddListOption = (nodeId: string, sectionIndex: number) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const options = Array.isArray(node.data?.options) ? [...node.data.options] : [];
        if (options[sectionIndex]) {
          options[sectionIndex].options.push(`Option ${options[sectionIndex].options.length + 1}`);
        }
        return {
          ...node,
          data: {
            ...node.data,
            options: options
          }
        };
      })
    );
  };

  const handleRemoveListOption = (nodeId: string, sectionIndex: number, optionIndex: number) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const options = Array.isArray(node.data?.options) ? [...node.data.options] : [];
        if (options[sectionIndex]) {
          options[sectionIndex].options.splice(optionIndex, 1);
          const optionHandle = `${nodeId}-options-${sectionIndex}-${optionIndex}`;
          setEditableEdges((prevEdges) => 
            prevEdges.filter((edge) => edge.sourceHandle !== optionHandle)
          );
        }
        return {
          ...node,
          data: {
            ...node.data,
            options: options
          }
        };
      })
    );
  };

  const handleAddListSection = (nodeId: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const options = Array.isArray(node.data?.options) ? [...node.data.options] : [];
        options.push({ title: `Section ${options.length + 1}`, options: ['Option 1'] });
        return {
          ...node,
          data: {
            ...node.data,
            options: options
          }
        };
      })
    );
  };

  const handleRemoveListSection = (nodeId: string, sectionIndex: number) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const options = Array.isArray(node.data?.options) ? [...node.data.options] : [];
        options.splice(sectionIndex, 1);
        return {
          ...node,
          data: {
            ...node.data,
            options: options
          }
        };
      })
    );
  };

  const handleListSectionTitleChange = (nodeId: string, sectionIndex: number, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        const options = Array.isArray(node.data?.options) ? [...node.data.options] : [];
        if (options[sectionIndex]) {
          options[sectionIndex].title = value;
        }
        return {
          ...node,
          data: {
            ...node.data,
            options: options
          }
        };
      })
    );
  };

  // Edge/Connection management
  const handleAddEdge = (sourceId: string, sourceHandle: string, targetId: string) => {
    if (!targetId) return;
    
    const existingEdge = editableEdges.find(
      (e) => e.source === sourceId && e.sourceHandle === sourceHandle
    );

    if (existingEdge) {
      setEditableEdges((prev) =>
        prev.map((e) =>
          e.source === sourceId && e.sourceHandle === sourceHandle
            ? { ...e, target: targetId }
            : e
        )
      );
    } else {
      const newEdge: Edge = {
        id: `${sourceId}-${targetId}-${Date.now()}`,
        source: sourceId,
        target: targetId,
        sourceHandle: sourceHandle,
        type: 'custom',
        style: { strokeWidth: 3 },
        markerEnd: { type: 'arrowclosed' },
        data: {
          onDelete: () => {},
          onSelect: () => {},
          isSelected: false,
        },
      };
      setEditableEdges((prev) => [...prev, newEdge]);
    }
  };

  const handleRemoveEdge = (sourceId: string, sourceHandle: string) => {
    setEditableEdges((prev) =>
      prev.filter((edge) => !(edge.source === sourceId && edge.sourceHandle === sourceHandle))
    );
  };

  const getPrevNodes = (nodeId: string) => {
    return editableEdges
      .filter((edge) => edge.target === nodeId)
      .map((edge) => ({ 
        source: edge.source, 
        handle: edge.sourceHandle || 'default',
        fullEdge: edge 
      }));
  };

  const getNextNodes = (nodeId: string) => {
    return editableEdges
      .filter((edge) => edge.source === nodeId)
      .map((edge) => ({ 
        target: edge.target, 
        handle: edge.sourceHandle || 'default',
        fullEdge: edge 
      }));
  };

  const getHeaderType = (node: Node) => {
    const rawType = node.data?.headerType?.value || '';
    return rawType.toLowerCase();
  };

  const getHeaderContent = (node: Node) => {
    const type = getHeaderType(node);
    if (
      ["document", "video", "image", "url"].includes(type) &&
      node.data?.media?.s3_object?.url
    ) {
      return node.data.media.s3_object.url;
    }
    return node.data?.header || '';
  };

  const getMediaUrl = (node: Node) => {
    return node.data?.media?.s3_object?.url || node.data?.media?.url || '';
  };

  const getMediaFilename = (node: Node) => {
    return node.data?.media?.filename || node.data?.media?.s3_object?.filename || '';
  };

  const getNodeTypeLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const hasHeader = (node: Node) => {
    return NODE_TYPES_WITH_HEADER.includes(node.type);
  };

  const hasFooter = (node: Node) => {
    return NODE_TYPES_WITH_FOOTER.includes(node.type);
  };

  const isMediaNode = (node: Node) => {
    return MEDIA_NODE_TYPES.includes(node.type);
  };

  const toggleExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getAvailableTargets = (sourceId: string) => {
    return editableNodes
      .filter((n) => n.id !== sourceId)
      .map((n) => ({ label: `${n.id} (${getNodeTypeLabel(n.type)})`, value: n.id }));
  };

  // Delete a single node
  const handleDeleteNode = (nodeId: string) => {
    if (window.confirm(`Are you sure you want to delete node "${nodeId}"? This will also remove all connected edges.`)) {
      const updatedNodes = editableNodes.filter((node) => node.id !== nodeId);
      const updatedEdges = editableEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
      
      setEditableNodes(updatedNodes);
      setEditableEdges(updatedEdges);
      onUpdateNodes(updatedNodes);
      if (onUpdateEdges) {
        onUpdateEdges(updatedEdges);
      }
      
      setSnackbar({
        open: true,
        message: `Node "${nodeId}" deleted successfully`,
        severity: 'success'
      });
    }
  };

  // Add a new node
  const handleAddNewNode = () => {
    const newId = `node_${Date.now()}`;
    const newNode: Node = {
      id: newId,
      type: newNodeType,
      position: { x: 100, y: 100 },
      data: {
        id: newId,
        type: newNodeType,
        header: '',
        body: '',
        footer: '',
        text: '',
        sourceType: '',
        onChange: () => {},
        onNodesDelete: () => {},
        onOutputClick: () => {},
        media: {},
        ...(newNodeType === 'button' && { buttons: ['Button 1', 'Button 2', 'Button 3'] }),
        ...(newNodeType === 'list' && { options: [{ title: 'Section 1', options: ['Option 1', 'Option 2'] }] }),
        ...(newNodeType === 'intervention' && {
          intervention: [
            { type: 'click_to_call', value: 'Click to call', label: 'Click to call', enabled: false },
            { type: 'request_to_callback', value: 'Request to call back', label: 'Request to call back', enabled: false },
            { type: 'chat_with_agent', value: 'Chat with Agent', label: 'Chat with Agent', enabled: false }
          ]
        })
      }
    };
    
    const updatedNodes = [...editableNodes, newNode];
    setEditableNodes(updatedNodes);
    setShowNewNodeRow(false);
    setNewNodeType('text');
    onUpdateNodes(updatedNodes);
    
    setSnackbar({
      open: true,
      message: `New "${getNodeTypeLabel(newNodeType)}" node added successfully`,
      severity: 'success'
    });
  };

  // Duplicate a node
  const handleDuplicateNode = (nodeId: string) => {
    const sourceNode = editableNodes.find(n => n.id === nodeId);
    if (!sourceNode) return;

    const newId = `node_${Date.now()}_copy`;
    const duplicatedNode: Node = {
      ...sourceNode,
      id: newId,
      position: {
        x: sourceNode.position.x + 50,
        y: sourceNode.position.y + 50
      },
      data: {
        ...sourceNode.data,
        id: newId,
        onChange: undefined,
        onNodesDelete: undefined,
        onOutputClick: undefined,
      }
    };

    const updatedNodes = [...editableNodes, duplicatedNode];
    setEditableNodes(updatedNodes);
    onUpdateNodes(updatedNodes);
    
    setSnackbar({
      open: true,
      message: `Node "${nodeId}" duplicated successfully as "${newId}"`,
      severity: 'success'
    });
  };

  // Open full node editor
  const handleOpenNodeEditor = (nodeId: string) => {
    if (onEditNode) {
      onEditNode(nodeId);
      onClose();
    } else {
      setSnackbar({
        open: true,
        message: 'Full editor not available. Please use the canvas to edit.',
        severity: 'info'
      });
    }
  };

  // Clear media from node
  const handleClearMedia = (nodeId: string) => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;
        return {
          ...node,
          data: {
            ...node.data,
            media: {}
          }
        };
      })
    );
    showSnackBar('Media cleared successfully', 'info');
  };

  const handleSave = () => {
    onUpdateNodes(editableNodes);
    if (onUpdateEdges) {
      onUpdateEdges(editableEdges);
    }
    setIsEditable(false);
    setEditingRow(null);
    setSnackbar({
      open: true,
      message: 'Changes saved successfully!',
      severity: 'success'
    });
    onClose();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
  const handleToggleFullscreen = () => setFullscreen((prev) => !prev);

  const columnStyle = {
    minWidth: 80,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  };

  // Render a single node row
  const renderNodeRow = (node: Node, index: number) => {
    const isRowEditing = editingRow === node.id;
    const isTextNode = node.type === 'text';
    const isButtonNode = node.type === 'button';
    const isListNode = node.type === 'list';
    const isExpanded = expandedNodes.has(node.id);
    const isMedia = isMediaNode(node);
    const isUploading = uploadingNode === node.id;
    const config = isMedia ? FILE_CONFIG[node.type as keyof typeof FILE_CONFIG] : null;
    const mediaUrl = getMediaUrl(node);
    const mediaFilename = getMediaFilename(node);
    
    const buttons = Array.isArray(node.data?.buttons) ? node.data.buttons : [];
    const options = Array.isArray(node.data?.options) ? node.data.options : [];

    const prevNodes = getPrevNodes(node.id);
    const nextNodes = getNextNodes(node.id);
    const availableTargets = getAvailableTargets(node.id);

    let headerDisplay = '-';
    let headerEditValue = '';
    
    if (hasHeader(node)) {
      if (isMedia) {
        headerDisplay = mediaUrl || '-';
        headerEditValue = mediaUrl;
      } else {
        headerDisplay = getHeaderContent(node) || '-';
        headerEditValue = getHeaderContent(node);
      }
    }

    return (
      <React.Fragment key={node.id}>
        <TableRow 
          sx={{ 
            '&:hover': { bgcolor: '#f5f5f5' },
            bgcolor: isRowEditing ? '#fff3e0' : 'inherit',
          }}
        >
          <TableCell>{index + 1}</TableCell>
          <TableCell>
            {isEditable && isRowEditing ? (
              <TextField
                fullWidth
                variant="standard"
                size="small"
                value={node.id}
                onChange={(e) => {
                  const newId = e.target.value;
                  setEditableNodes((prev) =>
                    prev.map((n) =>
                      n.id === node.id ? { ...n, id: newId } : n
                    )
                  );
                  setEditableEdges((prev) =>
                    prev.map((edge) => ({
                      ...edge,
                      source: edge.source === node.id ? newId : edge.source,
                      target: edge.target === node.id ? newId : edge.target,
                    }))
                  );
                }}
              />
            ) : (
              <Chip label={node.id} size="small" variant="outlined" />
            )}
          </TableCell>
          <TableCell>
            {isEditable && isRowEditing ? (
              <FormControl fullWidth size="small" variant="standard">
                <Select
                  value={node.type}
                  onChange={(e) => handleNodeTypeChange(node.id, e.target.value)}
                >
                  {NODE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {getNodeTypeLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Chip 
                label={getNodeTypeLabel(node.type)} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            )}
          </TableCell>
          <TableCell>{node.data?.nodeCount || '-'}</TableCell>
          
          {/* Header Type */}
          <TableCell>
            {hasHeader(node) && (
              isEditable && isRowEditing ? (
                <FormControl fullWidth size="small" variant="standard">
                  <Select
                    value={node.data?.headerType?.value || 'text'}
                    onChange={(e) => handleHeaderTypeChange(node.id, e.target.value)}
                  >
                    {HEADER_TYPES.map((ht) => (
                      <MenuItem key={ht.value} value={ht.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {ht.icon}
                          {ht.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip 
                  label={node.data?.headerType?.label || 'Text'} 
                  size="small" 
                  variant="outlined"
                  icon={HEADER_TYPES.find(h => h.value === (node.data?.headerType?.value || 'text'))?.icon}
                />
              )
            )}
            {!hasHeader(node) && '-'}
          </TableCell>
          
          {/* Header / Media URL with Upload */}
          <TableCell>
            {hasHeader(node) ? (
              isEditable && isRowEditing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <TextField
                    fullWidth
                    variant="standard"
                    size="small"
                    value={headerEditValue}
                    onChange={(e) => {
                      if (isMedia) {
                        handleMediaUrlChange(node.id, e.target.value);
                      } else {
                        handleFieldChange(node.id, 'header', e.target.value);
                      }
                    }}
                    placeholder={isMedia ? `${getNodeTypeLabel(node.type)} URL` : "Header text"}
                  />

                  {/* Only show upload button for media header types, not text */}
                  {isMedia && config && node.data?.headerType?.value !== 'text' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={config.accept}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(node.id, file, node.type);
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        sx={{ textTransform: 'none' }}
                      >
                        {isUploading ? 'Uploading...' : `Upload ${config.label}`}
                      </Button>
                      {mediaUrl && (
                        <Tooltip title="Clear media">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleClearMedia(node.id)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}

                  {/* Upload progress - only for media types */}
                  {isUploading && node.data?.headerType?.value !== 'text' && (
                    <Box sx={{ width: '100%', mt: 0.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {uploadProgress}% uploaded
                      </Typography>
                    </Box>
                  )}

                  {/* Show uploaded filename - only for media types */}
                  {isMedia && mediaFilename && node.data?.headerType?.value !== 'text' && (
                    <Chip
                      label={`📎 ${mediaFilename}`}
                      size="small"
                      variant="outlined"
                      color="success"
                      onDelete={() => handleClearMedia(node.id)}
                    />
                  )}
                </Box>
              ) : (
                <Box>
                  {isMedia && mediaUrl && node.data?.headerType?.value !== 'text' ? (
                    <Box>
                      <Tooltip title={mediaUrl}>
                        <span>{mediaFilename || mediaUrl.substring(0, 50)}{mediaUrl.length > 50 ? '...' : ''}</span>
                      </Tooltip>
                      {mediaFilename && (
                        <Chip
                          label="✓ Uploaded"
                          size="small"
                          color="success"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Tooltip title={headerDisplay}>
                      <span>{headerDisplay.substring(0, 50)}{headerDisplay.length > 50 ? '...' : ''}</span>
                    </Tooltip>
                  )}
                </Box>
              )
            ) : '-'}
          </TableCell>

          {/* Body */}
          <TableCell sx={{ minWidth: 200 }}>
            {isEditable && isRowEditing ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="standard"
                size="small"
                value={node.data?.body || ''}
                error={(node.data?.body || '').length > 3000}
                helperText={
                  (node.data?.body || '').length > 3000
                    ? 'Body exceeds 3000 characters!'
                    : `${(node.data?.body || '').length}/3000`
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 3000) {
                    handleFieldChange(node.id, 'body', newValue);
                  }
                }}
                placeholder="Enter body text"
              />
            ) : (
              <Tooltip title={node.data?.body || ''}>
                <span>{(node.data?.body || '').substring(0, 100)}{(node.data?.body || '').length > 100 ? '...' : ''}</span>
              </Tooltip>
            )}
          </TableCell>
          
          {/* Footer */}
          <TableCell>
            {hasFooter(node) && !isTextNode ? (
              isEditable && isRowEditing ? (
                <TextField
                  fullWidth
                  variant="standard"
                  size="small"
                  value={node.data?.footer || ''}
                  onChange={(e) =>
                    handleFieldChange(node.id, 'footer', e.target.value)
                  }
                  placeholder="Footer text"
                />
              ) : (
                node.data?.footer || '-'
              )
            ) : '-'}
          </TableCell>
          
          {/* Buttons/Options */}
          <TableCell sx={{ minWidth: 200, maxWidth: 300 }}>
            <Box>
              {isButtonNode && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={`${buttons.length}/3 buttons`} 
                      size="small" 
                      color="info" 
                    />
                    {buttons.length > 0 && (
                      <IconButton size="small" onClick={() => toggleExpand(node.id)}>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </Box>
                  <Collapse in={isExpanded}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {buttons.map((btn, idx) => {
                        const buttonHandle = `${node.id}-button-${idx}`;
                        const existingEdge = editableEdges.find(
                          (e) => e.source === node.id && e.sourceHandle === buttonHandle
                        );
                        const currentTarget = existingEdge?.target || '';

                        return (
                          <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              {isEditable && isRowEditing ? (
                                <>
                                  <TextField
                                    size="small"
                                    variant="standard"
                                    value={btn}
                                    onChange={(e) => handleButtonChange(node.id, idx, e.target.value)}
                                    placeholder={`Button ${idx + 1}`}
                                    sx={{ flex: 1 }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveButton(node.id, idx)}
                                    disabled={buttons.length <= 1}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </>
                              ) : (
                                <Chip label={btn || `Button ${idx + 1}`} size="small" variant="outlined" />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ minWidth: 60 }}>Connect to:</Typography>
                              <FormControl size="small" sx={{ flex: 1 }}>
                                <Select
                                  value={currentTarget}
                                  onChange={(e) => {
                                    const targetId = e.target.value;
                                    if (targetId) {
                                      handleAddEdge(node.id, buttonHandle, targetId);
                                    } else {
                                      handleRemoveEdge(node.id, buttonHandle);
                                    }
                                  }}
                                  displayEmpty
                                  variant="standard"
                                >
                                  <MenuItem value="">Disconnect</MenuItem>
                                  {availableTargets.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>
                        );
                      })}
                      {isEditable && isRowEditing && buttons.length < 3 && (
                        <Button size="small" variant="outlined" onClick={() => handleAddButton(node.id)} startIcon={<Add />}>
                          Add Button
                        </Button>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {isListNode && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={`${options.flatMap(o => o.options).length} options in ${options.length} sections`} 
                      size="small" 
                      color="info" 
                    />
                    {options.length > 0 && (
                      <IconButton size="small" onClick={() => toggleExpand(node.id)}>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </Box>
                  <Collapse in={isExpanded}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {options.map((section, sectionIdx) => (
                        <Box key={sectionIdx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                          {isEditable && isRowEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              variant="standard"
                              value={section.title || ''}
                              onChange={(e) => handleListSectionTitleChange(node.id, sectionIdx, e.target.value)}
                              placeholder="Section title"
                              sx={{ mb: 1 }}
                            />
                          ) : (
                            <Typography variant="caption" fontWeight="bold">
                              {section.title || `Section ${sectionIdx + 1}`}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                            {section.options.map((opt, optIdx) => {
                              const optionHandle = `${node.id}-options-${sectionIdx}-${optIdx}`;
                              const existingEdge = editableEdges.find(
                                (e) => e.source === node.id && e.sourceHandle === optionHandle
                              );
                              const currentTarget = existingEdge?.target || '';

                              return (
                                <Box key={optIdx} sx={{ border: '1px solid #f0f0f0', borderRadius: 1, p: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isEditable && isRowEditing ? (
                                      <>
                                        <TextField
                                          size="small"
                                          variant="standard"
                                          value={opt}
                                          onChange={(e) => handleListOptionChange(node.id, sectionIdx, optIdx, e.target.value)}
                                          placeholder={`Option ${optIdx + 1}`}
                                          sx={{ flex: 1 }}
                                        />
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleRemoveListOption(node.id, sectionIdx, optIdx)}
                                          disabled={section.options.length <= 1}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <Chip label={opt || `Option ${optIdx + 1}`} size="small" variant="outlined" />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ minWidth: 60 }}>Connect to:</Typography>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                      <Select
                                        value={currentTarget}
                                        onChange={(e) => {
                                          const targetId = e.target.value;
                                          if (targetId) {
                                            handleAddEdge(node.id, optionHandle, targetId);
                                          } else {
                                            handleRemoveEdge(node.id, optionHandle);
                                          }
                                        }}
                                        displayEmpty
                                        variant="standard"
                                      >
                                        <MenuItem value="">Disconnect</MenuItem>
                                        {availableTargets.map((opt) => (
                                          <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Box>
                              );
                            })}
                            {isEditable && isRowEditing && section.options.length < 10 && (
                              <Button size="small" variant="outlined" onClick={() => handleAddListOption(node.id, sectionIdx)} startIcon={<Add />}>
                                Add Option
                              </Button>
                            )}
                          </Box>
                          {isEditable && isRowEditing && options.length > 1 && (
                            <Button size="small" color="error" onClick={() => handleRemoveListSection(node.id, sectionIdx)}>
                              Remove Section
                            </Button>
                          )}
                        </Box>
                      ))}
                      {isEditable && isRowEditing && options.length < 10 && (
                        <Button size="small" variant="outlined" onClick={() => handleAddListSection(node.id)} startIcon={<Add />}>
                          Add Section
                        </Button>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {!isButtonNode && !isListNode && (
                <Typography variant="caption" color="text.secondary">-</Typography>
              )}
            </Box>
          </TableCell>
          
          <TableCell>
            {isEditable && isRowEditing ? (
              <TextField
                fullWidth
                variant="standard"
                size="small"
                type="number"
                value={node.position.x}
                onChange={(e) =>
                  handlePositionChange(node.id, 'x', parseFloat(e.target.value) || 0)
                }
              />
            ) : (
              Math.round(node.position.x)
            )}
          </TableCell>
          <TableCell>
            {isEditable && isRowEditing ? (
              <TextField
                fullWidth
                variant="standard"
                size="small"
                type="number"
                value={node.position.y}
                onChange={(e) =>
                  handlePositionChange(node.id, 'y', parseFloat(e.target.value) || 0)
                }
              />
            ) : (
              Math.round(node.position.y)
            )}
          </TableCell>
          
          <TableCell>
            {prevNodes.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {prevNodes.map((pn, idx) => (
                  <Chip 
                    key={idx} 
                    label={`${pn.source}`} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                  />
                ))}
              </Box>
            ) : '-'}
          </TableCell>
          
          <TableCell>
            {nextNodes.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {nextNodes.map((nn, idx) => (
                  <Chip 
                    key={idx} 
                    label={`${nn.target}`} 
                    size="small" 
                    variant="outlined" 
                    color="success"
                  />
                ))}
              </Box>
            ) : '-'}
          </TableCell>
          
          {/* Actions */}
          <TableCell sx={{ position: 'sticky', right: 0, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Tooltip title="Open Full Editor (Upload Images, Videos, etc.)">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenNodeEditor(node.id)}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>

              {isEditable ? (
                <>
                  {isRowEditing ? (
                    <>
                      <Tooltip title="Save Changes">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setEditingRow(null);
                            setSnackbar({
                              open: true,
                              message: `Changes saved for node "${node.id}"`,
                              severity: 'success'
                            });
                          }}
                        >
                          <Save fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel Edit">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setEditingRow(null);
                            const originalNode = nodes.find(n => n.id === node.id);
                            if (originalNode) {
                              setEditableNodes((prev) =>
                                prev.map((n) =>
                                  n.id === node.id ? { ...originalNode } : n
                                )
                              );
                            }
                          }}
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Edit Row">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => setEditingRow(node.id)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate Node">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleDuplicateNode(node.id)}
                        >
                          <AddCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Node">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteNode(node.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              ) : (
                <Tooltip title="Enable Edit Mode to modify">
                  <IconButton size="small" disabled>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen={fullscreen} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>Bot Flow Table View</span>
            <Chip 
              label={`${editableNodes.length} nodes`} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={`${editableEdges.length} edges`} 
              size="small" 
              color="secondary" 
              variant="outlined" 
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} size="small"><Remove /></IconButton>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} size="small"><Add /></IconButton>
            </Tooltip>
            <Tooltip title="Toggle Fullscreen">
              <IconButton onClick={handleToggleFullscreen} size="small">
                {fullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Add New Node">
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<AddCircle />}
                onClick={() => setShowNewNodeRow(true)}
                disabled={showNewNodeRow}
              >
                Add Node
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={isEditable ? <Save /> : <Edit />}
              size="small"
              onClick={() => {
                if (isEditable) {
                  handleSave();
                } else {
                  setIsEditable(true);
                }
              }}
              color={isEditable ? 'success' : 'primary'}
            >
              {isEditable ? 'Save All' : 'Edit Mode'}
            </Button>
            {isEditable && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Cancel />}
                onClick={() => {
                  setEditableNodes(nodes.map((n) => ({ ...n })));
                  setEditableEdges(edges.map((e) => ({ ...e })));
                  setIsEditable(false);
                  setEditingRow(null);
                  setShowNewNodeRow(false);
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: fullscreen ? '100%' : 600, padding: 0 }}>
          <Box
            sx={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              overflow: 'auto',
              height: '100%',
              padding: 1,
            }}
          >
            <TableContainer component={Paper}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...columnStyle, minWidth: 40 }}>#</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 100 }}>Node ID</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 100 }}>Type</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 50 }}>Count</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 100 }}>Header Type</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 200 }}>Header / Media URL</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 200 }}>Body</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 80 }}>Footer</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 200 }}>Buttons/Options</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 60 }}>Pos X</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 60 }}>Pos Y</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 100 }}>Prev Nodes</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 100 }}>Next Nodes</TableCell>
                    <TableCell sx={{ ...columnStyle, minWidth: 160, position: 'sticky', right: 0, bgcolor: 'background.paper' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Add New Node Row */}
                  {showNewNodeRow && (
                    <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                      <TableCell colSpan={14}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', p: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Node Type</InputLabel>
                            <Select
                              value={newNodeType}
                              onChange={(e) => setNewNodeType(e.target.value)}
                              label="Node Type"
                            >
                              {NODE_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {getNodeTypeLabel(type)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            placeholder="Pos X"
                            type="number"
                            defaultValue={100}
                            sx={{ width: 80 }}
                          />
                          <TextField
                            size="small"
                            placeholder="Pos Y"
                            type="number"
                            defaultValue={100}
                            sx={{ width: 80 }}
                          />
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<Save />}
                            onClick={handleAddNewNode}
                          >
                            Create
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setShowNewNodeRow(false);
                              setNewNodeType('text');
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Existing Nodes */}
                  {editableNodes.map((node, index) => renderNodeRow(node, index))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              label={`Total Nodes: ${editableNodes.length}`} 
              size="small" 
              color="info" 
            />
            <Chip 
              label={`Edges: ${editableEdges.length}`} 
              size="small" 
              color="info" 
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined" size="small">
              Close
            </Button>
            {isEditable && (
              <Button variant="contained" color="success" onClick={handleSave} size="small" startIcon={<Save />}>
                Apply All Changes
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShowTableModal;